class MainCart extends HTMLElement {
  static TAG = 'main-cart';

  static SELECTORS = {
    aside: '.cart__right',
    changeAnchor: 'a[href*="cart/change"]',
    lineKeyInput: 'input[data-line-key]',
    lineItem: 'line-item',
    quantitySelector: 'quantity-selector',
    errorContainer: '.line-item__error-container',
    section: '.shopify-section'
  };

  static EVENTS = {
    prepareSections: 'cart:prepare-bundled-sections',
    change: 'cart:change',
    lineItemChange: 'line-item:change',
    lineItemWillChange: 'line-item:will-change',
    loadingStart: 'theme:loading:start',
    loadingEnd: 'theme:loading:end'
  };

  static BASE_EVENT = 'line-item:change';
  static SECTION_PREFIX = 'shopify-section-';
  static CHANGE_PATH = 'cart/change.js';
  static LAYOUT = { breakpoint: 1000, offset: 120 };
  static POSITION = { fixed: 'fixed', static: 'static', relative: 'relative' };

  static ERROR_ICON = '<svg width="13" viewBox="0 0 13 13"><circle cx="6.5" cy="6.5" r="5.5" stroke="#fff" stroke-width="2"></circle><circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width=".7"></circle><path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"></path><path fill="#fff" stroke="#EB001B" stroke-width=".7" d="M5.874 3.178h-.359l.01.359.1 4.044.008.341h1.736l.008-.341.1-4.044.01-.359H5.873Zm.627 6.833c.56 0 1.03-.432 1.03-1.006s-.47-1.006-1.03-1.006-1.031.432-1.031 1.006.47 1.006 1.03 1.006Z"></path></svg>';

  connectedCallback () {
    this._sectionId = this.getAttribute('section-id')
      || this.closest(MainCart.SELECTORS.section)?.id.replace(MainCart.SECTION_PREFIX, '')
      || '';

    this._abort = new AbortController();
    const signal = this._abort.signal;

    // Own the cart-page mutations so the theme's full-page reload never runs — we
    // AJAX-swap the section and dispatch cart:change (tracking + drawer re-render).
    this.addEventListener('click', this._onAnchorClick.bind(this), { capture: true, signal });
    this.addEventListener('change', this._onInputChange.bind(this), { capture: true, signal });
    document.addEventListener(MainCart.EVENTS.prepareSections, this._onPrepareSections.bind(this), { signal });
    document.addEventListener(MainCart.EVENTS.change, this._onCartChange.bind(this), { signal });
    window.addEventListener('scroll', this._positionTotals, { signal });
    window.addEventListener('resize', this._positionTotals, { signal });

    this._cacheAside();
    this._positionTotals();
  }

  disconnectedCallback () {
    this._abort?.abort();
  }

  // ─── Interaction interception ─────────────────────────────────────────────────
  _onAnchorClick (event) {
    const anchor = event.target.closest?.(MainCart.SELECTORS.changeAnchor);
    if (!anchor || !this.contains(anchor)) return;
    const url = new URL(anchor.href);
    const lineKey = url.searchParams.get('id');
    const quantity = parseInt(url.searchParams.get('quantity'), 10);
    if (!lineKey || Number.isNaN(quantity)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this._changeQuantity(lineKey, quantity, anchor.closest(MainCart.SELECTORS.lineItem));
  }

  _onInputChange (event) {
    const input = event.target;
    if (!input.matches?.(MainCart.SELECTORS.lineKeyInput)) return;
    const lineKey = input.dataset.lineKey;
    const quantity = parseInt(input.value, 10);
    if (!lineKey || Number.isNaN(quantity)) return;
    event.stopImmediatePropagation();
    this._changeQuantity(lineKey, quantity, input.closest(MainCart.SELECTORS.lineItem));
  }

  // ─── Mutation (replicates the theme's changeLineItemQuantity, minus the reload) ─
  async _changeQuantity (lineKey, quantity, lineItem) {
    document.documentElement.dispatchEvent(new CustomEvent(MainCart.EVENTS.loadingStart, { bubbles: true }));
    lineItem?.dispatchEvent(new CustomEvent(MainCart.EVENTS.lineItemWillChange, { bubbles: true, detail: { targetQuantity: quantity } }));

    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent(MainCart.EVENTS.prepareSections, { bubbles: true, detail: { sections } }));

    let response;
    try {
      response = await fetch(`${Shopify.routes.root}${MainCart.CHANGE_PATH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity, sections: sections.join(',') })
      });
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent(MainCart.EVENTS.loadingEnd, { bubbles: true }));
    }

    const cart = await response.json();
    if (!response.ok) {
      this._showLineError(lineItem, cart);
      return;
    }
    this._dispatchChange(cart, lineKey, lineItem);
  }

  _dispatchChange (cart, lineKey, lineItem) {
    const line = (cart.items || []).find((item) => item.key === lineKey);
    lineItem?.dispatchEvent(new CustomEvent(MainCart.EVENTS.lineItemChange, {
      bubbles: true,
      detail: { quantity: line ? line.quantity : 0, cart }
    }));
    document.documentElement.dispatchEvent(new CustomEvent(MainCart.EVENTS.change, {
      bubbles: true,
      detail: { baseEvent: MainCart.BASE_EVENT, cart }
    }));
  }

  _showLineError (lineItem, responseContent) {
    const container = lineItem?.querySelector(MainCart.SELECTORS.errorContainer);
    if (container) {
      container.innerHTML = '';
      container.insertAdjacentHTML('afterbegin', `<p class="h-stack gap-1.5 text-sm text-error" role="alert">${MainCart.ERROR_ICON} ${responseContent.description || ''}</p>`);
    }
    lineItem?.querySelector(MainCart.SELECTORS.quantitySelector)?.restoreDefaultValue?.();
  }

  // ─── Section rendering ────────────────────────────────────────────────────────
  _onPrepareSections (event) {
    if (this._sectionId) event.detail?.sections?.push(this._sectionId);
  }

  _onCartChange (event) {
    const html = event.detail?.cart?.sections?.[this._sectionId];
    if (!html) return;
    const rendered = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById(`${MainCart.SECTION_PREFIX}${this._sectionId}`)
      ?.querySelector(MainCart.TAG);
    if (!rendered) return;
    this.replaceChildren(...document.createRange().createContextualFragment(rendered.innerHTML).children);
    this._cacheAside();
    this._positionTotals();
  }

  // ─── Sticky totals column ─────────────────────────────────────────────────────
  _cacheAside () {
    this._aside = this.querySelector(MainCart.SELECTORS.aside);
    this._asideTop = this._aside ? this._aside.offsetTop : 0;
  }

  _positionTotals = () => {
    if (!this._aside) return;
    const { innerWidth: w, innerHeight: h, scrollY: y } = window;
    const shouldFix = w <= MainCart.LAYOUT.breakpoint
      && h < this._asideTop
      && (h + y - MainCart.LAYOUT.offset) <= this._asideTop;
    if (shouldFix) {
      this._aside.classList.remove(MainCart.POSITION.relative);
      this._aside.classList.add(MainCart.POSITION.fixed);
    } else {
      this._aside.classList.remove(MainCart.POSITION.fixed);
      this._aside.classList.add(MainCart.POSITION.static);
    }
  };
}

if (!window.customElements.get(MainCart.TAG)) {
  window.customElements.define(MainCart.TAG, MainCart);
}
